let wasmFactory = null;
let wasmReady = false;

const wasmBase = new URL("./", import.meta.url).href;

export async function loadWasm() {
  const statusText = document.getElementById("status-text");
  statusText.textContent = "Loading OpenSCAD WASM...";

  const OpenSCAD = (await import(wasmBase + "openscad.js")).default;
  const { addFonts } = await import(wasmBase + "openscad.fonts.js");

  wasmFactory = async () => {
    const instance = await OpenSCAD({
      noInitialRun: true,
      locateFile: (name) => "dist/" + name,
      print: (t) => console.log("[openscad]", t),
      printErr: (t) => console.error("[openscad]", t),
    });
    addFonts(instance);
    return instance;
  };

  wasmReady = true;
  statusText.textContent = "OpenSCAD WASM loaded.";
}

export async function createInstance() {
  if (!wasmFactory) {
    throw new Error("WASM not loaded yet");
  }
  return wasmFactory();
}

export async function mountFiles(instance, files, baseUrl) {
  const promises = files.map(async (file) => {
    const fileUrl = `${baseUrl}/${file}`;
    const response = await fetch(fileUrl);
    const buf = await response.arrayBuffer();
    const path = "/" + file;
    const parts = path.split("/");
    let current = "";
    for (let i = 1; i < parts.length - 1; i++) {
      current += "/" + parts[i];
      try { instance.FS.mkdir(current); } catch (e) {}
    }
    try {
      instance.FS.writeFile(path, new Uint8Array(buf));
    } catch (e) {
      console.error(`Failed to write ${path}:`, e);
    }
  });
  await Promise.all(promises);
}

export function parseParams(scadSource) {
  const vars = {};
  const lines = scadSource.split('\n');
  let currentGroup = '';

  for (const line of lines) {
    const trimmed = line.trim();

    const groupMatch = trimmed.match(/^\/\/\s*\[(.*?)\]\s*$/);
    if (groupMatch) {
      currentGroup = groupMatch[1].trim();
      continue;
    }

    const paramMatch = trimmed.match(/^(\w+)\s*=\s*([^;]+);\s*\/\/\s*\[(.+)\]\s*;?\s*$/);
    if (paramMatch) {
      const name = paramMatch[1];
      let valueStr = paramMatch[2].trim();
      const optionsStr = paramMatch[3];
      const options = parseParamOptions(optionsStr);
      const value = parseValue(valueStr, options);

      vars[name] = {
        name, value,
        type: options.type || guessType(value),
        caption: options.label || name,
        min: options.min, max: options.max, step: options.step,
        options: options.options,
        group: currentGroup || (options.group || null),
      };
      continue;
    }

    const vecMatch = trimmed.match(/^(\w+)\s*=\s*\[([^\]]+)\];\s*\/\/\s*\[(.+)\]\s*;?\s*$/);
    if (vecMatch) {
      const name = vecMatch[1];
      const vecStr = vecMatch[2];
      const optionsStr = vecMatch[3];
      const options = parseParamOptions(optionsStr);
      const values = vecStr.split(',').map(v => parseFloat(v.trim()));

      vars[name] = {
        name, value: values, type: 'vector',
        caption: options.label || name,
        min: options.min, max: options.max, step: options.step,
        options: options.options,
        group: currentGroup || (options.group || null),
      };
      continue;
    }
  }

  return { params: { vars } };
}

function parseParamOptions(str) {
  if (/^".*","/.test(str) || /^'.*','/.test(str)) {
    return {
      options: str.split(',').map(o => o.trim().replace(/^["']|["']$/g, '')),
    };
  }

  const result = {};
  const parts = splitOptions(str);

  if (parts.length > 0) result.label = parts[0].trim();
  if (parts.length > 1) result.type = parts[1].trim();
  if (parts.length > 2 && parts[2].trim() !== '') result.min = parseFloat(parts[2].trim());
  if (parts.length > 3 && parts[3].trim() !== '') result.max = parseFloat(parts[3].trim());
  if (parts.length > 4 && parts[4].trim() !== '') result.step = parseFloat(parts[4].trim());
  if (parts.length > 5) {
    const optMatch = parts[5].match(/^\[([^\]]+)\]$/);
    if (optMatch) result.options = optMatch[1].split(',').map(o => o.trim().replace(/^["']|["']$/g, ''));
  }
  if (parts.length > 6) result.group = parts[6].trim();

  return result;
}

function splitOptions(str) {
  const parts = [];
  let current = '';
  let inBrackets = false;
  for (let i = 0; i < str.length; i++) {
    const ch = str[i];
    if (ch === '[') { inBrackets = true; current += ch; }
    else if (ch === ']') { inBrackets = false; current += ch; }
    else if (ch === ',' && !inBrackets) { parts.push(current); current = ''; }
    else { current += ch; }
  }
  if (current) parts.push(current);
  return parts;
}

function parseValue(str, options) {
  if (str === 'true' || str === 'false') return str === 'true';
  if (!isNaN(str) && str !== '') return parseFloat(str);
  if ((str.startsWith('"') && str.endsWith('"')) || (str.startsWith("'") && str.endsWith("'"))) {
    return str.slice(1, -1);
  }
  if (str.startsWith('[')) {
    return str.slice(1, -1).split(',').map(v => parseFloat(v.trim()));
  }
  return str;
}

function guessType(value) {
  if (typeof value === 'boolean') return 'bool';
  if (typeof value === 'number') return 'number';
  if (Array.isArray(value)) return 'vector';
  return 'string';
}

export function extractParams(instance, mainFile) {
  const source = instance.FS.readFile("/" + mainFile, { encoding: 'utf8' });
  return parseParams(source);
}

export async function runOpenSCAD(instance, mainFile, vars, isPreview, exportFormat) {
  const newInst = await createInstance();
  const outFile = "output." + exportFormat;

  newInst.FS.writeFile("/current.scad", instance.FS.readFile("/" + mainFile));

  const args = ["/current.scad", "-o", outFile, "--export-format=" + exportFormat, "--enable=all"];

  if (isPreview) {
    args.push("--preview");
  }

  for (const [name, value] of Object.entries(vars)) {
    if (Array.isArray(value)) {
      args.push("-D" + name + "=[" + value.join(",") + "]");
    } else {
      args.push("-D" + name + "=" + JSON.stringify(value));
    }
  }

  const exitCode = newInst.callMain(args);
  if (exitCode !== 0) {
    throw new Error(`OpenSCAD exited with code ${exitCode}`);
  }

  const data = newInst.FS.readFile("/" + outFile, { encoding: "utf8" });
  newInst._free?.();
  return data;
}